/**
 * QuizSolveDescription Component
 * Ported from reference_legacy/quebon-lms-web/components/lesson/quiz-types/shared/quiz-solve-description.tsx
 * Modified to not depend on shadcn/ui Button component
 */

import { useTranslation } from 'react-i18next'
import { cn } from '../../../lib/utils'
import type { QuizSolveResultDescription, QuizMediaObject } from '../../../types/legacyQuiz'
import type { ImageSource } from '../../../utils/quiz-parser'
import { QuizImage } from './quiz-image'
import { DesmosGraph } from './desmos-graph'
import { formatText } from '../../../utils/quiz-parser'
import { motion } from 'framer-motion'
import { Lightbulb } from 'lucide-react'
import { QUIZ_EXPLANATION_STYLES } from './quiz-styles'
import { useQuizStyleVariant } from './quiz-style-context'

interface QuizSolveDescriptionProps {
  /** Solve result description (API response field name: description) */
  description?: QuizSolveResultDescription | null
  /** Commentary images for {image=n} tags in description.text */
  commentaryImages?: QuizMediaObject[] | null
  /** Variables for Desmos expression substitution (e.g., { _a: 5, _b: 3 }) */
  vars?: Record<string, unknown>
  /** Unique ID for React keys */
  id?: string
  /** Additional CSS classes */
  className?: string
  /** Whether to show the action button (정답보기/내 답안보기) */
  actionVisible?: boolean
  /** Label for the action button */
  actionLabel?: string
  /** Callback when action button is clicked */
  onActionClick?: () => void
}

const SHELL_REVEAL_DURATION = 0.22
const CONTENT_FADE_DURATION = 0.18

/**
 * QuizSolveDescription component
 * Renders the solve result description including text (with {image=n} support) and images
 */
export function QuizSolveDescription({
  description: descProp,
  commentaryImages,
  vars,
  id = 'solve-desc',
  className,
  actionVisible,
  actionLabel,
  onActionClick,
}: QuizSolveDescriptionProps) {
  const { t } = useTranslation('quiz')
  const variant = useQuizStyleVariant()
  const explanationStyles = QUIZ_EXPLANATION_STYLES[variant]
  // 해설은 항상 표시 (text, image가 없어도)
  // description이 없으면 기본값 사용
  const description: QuizSolveResultDescription = descProp ?? {
    text: undefined,
    image: null,
    imageMobile: null,
    desmos: null,
  }

  const hasText = description.text && description.text.trim().length > 0
  const hasImage = description.image || description.imageMobile
  const hasDesmos = !!description.desmos

  // Prepare images array for formatText
  const imgs: ImageSource[] | undefined = commentaryImages ?? undefined

  return (
    <motion.div
      data-testid="quiz-solve-description"
      initial={{ height: 0 }}
      animate={{ height: 'auto' }}
      transition={{ duration: SHELL_REVEAL_DURATION, ease: 'easeOut' }}
      className={cn(
        'overflow-hidden',
        variant === 'lc'
          ? 'rounded-[10px] border-[1.5px] border-lc-success-100 bg-lc-success-50'
          : 'bg-linear-to-r from-gray-50 rounded-2xl border border-gray-200 to-slate-50',
        className
      )}
    >
      <motion.div
        data-testid="quiz-solve-description-content"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{
          delay: SHELL_REVEAL_DURATION,
          duration: CONTENT_FADE_DURATION,
          ease: 'easeOut',
        }}
        className={cn(
          variant === 'lc'
            ? 'px-[10px] py-2'
            : 'flex items-start gap-3 p-4',
        )}
      >
        {variant === 'lc' ? (
          <div className="w-full space-y-3 text-[16px] font-normal leading-[1.6] text-lc-text-primary">
            {hasText ? formatText(description.text!, id, imgs) : null}
            {hasImage ? (
              <QuizImage
                image={description.image ?? undefined}
                imageMobile={description.imageMobile ?? undefined}
                alt={t('shared.explanationImage')}
                className="my-2"
              />
            ) : null}
            {hasDesmos ? (
              <div className="my-2 flex justify-center">
                <DesmosGraph
                  state={description.desmos!}
                  vars={vars}
                  className="rounded-xl"
                />
              </div>
            ) : null}
          </div>
        ) : (
          <>
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-lc-mint-900">
              <Lightbulb className="h-4 w-4 text-white" />
            </div>
            <div className="flex-1 space-y-3">
              <div className="flex h-8 items-center justify-between gap-2">
                <p className="text-lg font-semibold text-gray-700">{t('shared.explanation')}</p>
                {actionVisible && actionLabel && onActionClick ? (
                  <button
                    onClick={onActionClick}
                    className={explanationStyles.actionButton}
                  >
                    {actionLabel}
                  </button>
                ) : null}
              </div>

              {hasText ? (
                <div className="text-xl leading-relaxed text-gray-600">
                  {formatText(description.text!, id, imgs)}
                </div>
              ) : null}

              {hasImage ? (
                <QuizImage
                  image={description.image ?? undefined}
                  imageMobile={description.imageMobile ?? undefined}
                  alt={t('shared.explanationImage')}
                  className="my-2"
                />
              ) : null}

              {hasDesmos ? (
                <div className="my-2 flex justify-center">
                  <DesmosGraph
                    state={description.desmos!}
                    vars={vars}
                    className="rounded-xl"
                  />
                </div>
              ) : null}
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  )
}
